import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import { ProfileSchema, type ProfileRow, type ProfileUpdate } from "@/schemas/profile";
import { MOCK_TEAM } from "@/data/mock";
import { z } from "zod";

/**
 * Repository profiles — lecture & mise à jour uniquement.
 *
 * La CRÉATION d'utilisateurs se fait exclusivement via le Supabase Dashboard
 * (Authentication → Users → Add user). L'UI Skyline ne crée pas de users :
 * requiert l'API Admin (service_role key) qui ne doit jamais atterrir côté front.
 *
 * La SUPPRESSION d'utilisateurs suit le même principe : via dashboard.
 *
 * En mode mock (USE_SUPABASE=false), on utilise MOCK_TEAM comme source et on
 * ignore silencieusement les updates (juste merge in-memory). Ça permet de
 * tester l'UI sans toucher la DB.
 */

function fromMocks(): ProfileRow[] {
  // Map TeamMember to ProfileRow shape
  return MOCK_TEAM.map((m) => ({
    id: m.id,
    email: m.email,
    first_name: m.prenom,
    last_name: m.nom,
    role: m.role,
    telephone: m.telephone,
    societe: m.societe ?? null,
    specialites: m.specialites ?? null,
    statut: m.statut,
  }));
}

async function listFromSupabase(): Promise<ProfileRow[]> {
  // RPC `get_profiles_for_role` (SECURITY DEFINER, cf. migration 031) — bypasse
  // la RLS qui exigeait auth.uid() pour lire profiles. Sans ça, le mode test
  // (sans JWT) renvoyait [] et toutes les dropdowns d'équipe étaient vides.
  const { data, error } = await supabase.rpc("get_profiles_for_role", { p_role: "admin" });
  if (error) throw new Error(`listProfiles: ${error.message}`);
  return z.array(ProfileSchema).parse(data ?? []);
}

async function updateOnSupabase(id: string, patch: ProfileUpdate): Promise<ProfileRow> {
  // Admin peut tout modifier (RLS policy). Autre utilisateur ne peut changer
  // que son propre profil (sauf role, bloqué par la policy 005).
  const { data, error } = await supabase
    .from("profiles")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(`updateProfile(${id}): ${error.message}`);
  return ProfileSchema.parse(data);
}

export const profilesRepository = {
  async list(): Promise<ProfileRow[]> {
    if (!USE_SUPABASE) return fromMocks();
    return listFromSupabase();
  },

  async update(id: string, patch: ProfileUpdate): Promise<ProfileRow> {
    if (!USE_SUPABASE) {
      const p = fromMocks().find((x) => x.id === id);
      if (!p) throw new Error(`Profile introuvable: ${id}`);
      return { ...p, ...patch };
    }
    return updateOnSupabase(id, patch);
  },
};
